import React from 'react'
import './AnimatedIcon.css'

/* ═══════════════════════════════════════════════════════════
   AnimatedIcon
   ─────────────────────────────────────────────────────────
   A small, dependency-free, looping icon set used in place of
   emoji throughout the app.

   Why not real Lottie (.json / bodymovin) files here: without
   a real Lottie asset (exported from After Effects, or one
   downloaded from lottiefiles.com) there's nothing legitimate
   to render, and hand-writing bodymovin JSON by hand is easy to
   get subtly wrong in ways that only show up at runtime. These
   icons get the same result — small, looping, non-emoji — as
   plain animated SVG + CSS, which is fully reliable.

   Swapping in real Lottie later: this component's public API
   (`<AnimatedIcon name="download" size={18} />`) doesn't need to
   change. Inside ICONS, replace an entry's `paths` with:
     { lottie: '/icons/download.json' }
   and render it with `lottie-react`'s <Lottie path={...} loop />
   instead of the <svg> below — everywhere that calls
   <AnimatedIcon name="download" /> picks it up automatically.
═══════════════════════════════════════════════════════════ */

const ICONS = {
  /* ── Chrome / navigation ── */
  dashboard: { anim: 'pulse', paths: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  )},
  orders: { anim: 'sway', paths: (
    <>
      <path d="M5 3h14v17l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5V3z" />
      <line x1="8" y1="8" x2="16" y2="8" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </>
  )},
  finance: { anim: 'grow-y', paths: (
    <>
      <line x1="3" y1="21" x2="21" y2="21" />
      <rect x="5" y="13" width="4" height="8" />
      <rect x="10" y="9" width="4" height="12" />
      <rect x="15" y="5" width="4" height="16" />
    </>
  )},
  table: { anim: 'pulse', paths: (
    <>
      <rect x="3" y="6" width="18" height="4" rx="1" />
      <line x1="6" y1="10" x2="6" y2="20" />
      <line x1="18" y1="10" x2="18" y2="20" />
    </>
  )},
  expense: { anim: 'pulse', paths: (
    <>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18" />
      <circle cx="16" cy="13.5" r="1.3" fill="currentColor" stroke="none" />
    </>
  )},
  growth: { anim: 'draw', paths: (
    <>
      <polyline className="aicon-draw-target" points="3,17 9,11 13,15 21,6" />
      <polyline points="15,6 21,6 21,12" />
    </>
  )},
  edit: { anim: 'sway', paths: (
    <>
      <path d="M4 20l1-4L16 5l3 3L8 19l-4 1z" />
      <line x1="14" y1="7" x2="17" y2="10" />
    </>
  )},
  link: { anim: 'sway', paths: (
    <>
      <rect x="3" y="7" width="9" height="9" rx="3" transform="rotate(-45 7.5 11.5)" />
      <rect x="11.5" y="7.5" width="9" height="9" rx="3" transform="rotate(-45 16 12)" />
    </>
  )},

  /* ── Utility (hints, actions, tips) ── */
  pointer: { anim: 'bob-x', paths: (
    <>
      <line x1="4" y1="12" x2="18" y2="12" />
      <polyline points="13,7 18,12 13,17" />
    </>
  )},
  done: { anim: 'pulse', paths: (
    <>
      <circle cx="12" cy="12" r="9" />
      <polyline className="aicon-draw-target" points="7,12.5 10.5,16 17,8" />
    </>
  )},
  download: { anim: 'bob-y', paths: (
    <>
      <path d="M12 3v11" />
      <polyline points="7,10 12,15 17,10" />
      <path d="M4 19h16" />
    </>
  )},
  bulb: { anim: 'pulse', paths: (
    <>
      <path d="M9 18h6" />
      <path d="M10 21h4" />
      <path d="M12 3a6 6 0 0 0-3.5 10.9c.5.4.9 1.1.9 1.9V16h5.2v-.2c0-.8.4-1.5.9-1.9A6 6 0 0 0 12 3z" />
    </>
  )},
  ruler: { anim: 'grow-x', paths: (
    <>
      <rect x="2" y="9" width="20" height="6" rx="1" />
      <line x1="6" y1="9" x2="6" y2="12" />
      <line x1="10" y1="9" x2="10" y2="13" />
      <line x1="14" y1="9" x2="14" y2="12" />
      <line x1="18" y1="9" x2="18" y2="13" />
    </>
  )},
  frame: { anim: 'pulse', paths: (
    <rect x="5" y="3" width="14" height="18" rx="2" />
  )},

  /* ── QR design categories ── */
  menu: { anim: 'sway', paths: (
    <>
      <circle cx="12" cy="12" r="8" />
      <line x1="9" y1="7" x2="9" y2="17" />
      <line x1="15" y1="7" x2="15" y2="17" />
    </>
  )},
  cafe: { anim: 'none', paths: (
    <>
      <path d="M4 8h13v6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V8z" />
      <path d="M17 9h2a2.5 2.5 0 0 1 0 5h-2" />
      <path className="aicon-steam1" d="M9 5c0-1 1-1 1-2" />
      <path className="aicon-steam2" d="M13 5c0-1 1-1 1-2" />
    </>
  )},
  asian: { anim: 'sway', paths: (
    <>
      <path d="M3 12a9 4 0 0 0 18 0z" />
      <line x1="7" y1="12" x2="7" y2="3" />
      <line x1="17" y1="12" x2="15" y2="3" />
    </>
  )},
  italian: { anim: 'bob-y', paths: (
    <>
      <path d="M12 3l9 16H3z" />
      <circle cx="10" cy="14" r="1" fill="currentColor" stroke="none" />
      <circle cx="14" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
    </>
  )},
  bar: { anim: 'pulse', paths: (
    <>
      <path d="M4 4h16l-8 9z" />
      <line x1="12" y1="13" x2="12" y2="20" />
      <line x1="8" y1="20" x2="16" y2="20" />
    </>
  )},
  american: { anim: 'flicker', paths: (
    <path d="M12 2c2 3-1 4-1 7a3 3 0 0 0 6 0c0-1-.5-2-1-2 1 4-1 6-4 6a5 5 0 0 1-5-5c0-4 3-4 5-6z" />
  )},
  mediterranean: { anim: 'sway', paths: (
    <>
      <path d="M4 20C4 10 10 4 20 4c0 10-6 16-16 16z" />
      <line x1="4" y1="20" x2="14" y2="10" />
    </>
  )},
  modern: { anim: 'pulse', paths: (
    <rect x="5" y="5" width="14" height="14" rx="2" transform="rotate(45 12 12)" />
  )},

  /* ── Chrome toggles ── */
  'menu-open': { anim: 'none', paths: (
    <>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </>
  )},
  'menu-close': { anim: 'none', paths: (
    <>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </>
  )},
}

/* Maps each QR theme category to one of the icons above, so the
   ~20 themes share 8 recognizable category icons instead of each
   needing a bespoke one. */
export const CATEGORY_ICON = {
  Restaurant: 'menu',
  'Café': 'cafe',
  Asian: 'asian',
  Italian: 'italian',
  'Bar & Lounge': 'bar',
  American: 'american',
  Mediterranean: 'mediterranean',
  Modern: 'modern',
}

const AnimatedIcon = ({ name, size = 18, strokeWidth = 1.7, className = '', title }) => {
  const def = ICONS[name]
  if (!def) return null
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`aicon ${def.anim !== 'none' ? `aicon-${def.anim}` : ''} ${className}`}
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
    >
      {title && <title>{title}</title>}
      {def.paths}
    </svg>
  )
}

export default AnimatedIcon